import { useMemo, useState } from 'react';
import { Edit, Plus, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useBlocks, useDeleteBlock } from '../hooks/use-dossier-company';
import { BlockEditorDialog } from './block-editor-dialog';
import { BLOCK_CATEGORIES_ORDER, BLOCK_CATEGORY_LABELS } from './piece-types';
import type {
  BlockCategory,
  BlockStatus,
  KnowledgeBlock,
} from '../types/dossier-company.types';

const STATUS_LABELS: Record<BlockStatus, string> = {
  brouillon: 'Brouillon',
  actif: 'Actif',
  archive: 'Archivé',
};

export function BlocksTab() {
  const [category, setCategory] = useState<BlockCategory | 'ALL'>('ALL');
  const [status, setStatus] = useState<BlockStatus | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<KnowledgeBlock | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const filters = useMemo(
    () => ({
      ...(category !== 'ALL' && { category }),
      ...(status !== 'ALL' && { status }),
      ...(search.trim() && { search: search.trim() }),
    }),
    [category, status, search],
  );

  const { data, isLoading } = useBlocks(filters);
  const deleteBlock = useDeleteBlock();

  const groupedByCategory = useMemo(() => {
    if (!data) return {} as Record<BlockCategory, KnowledgeBlock[]>;
    return data.reduce<Record<string, KnowledgeBlock[]>>((acc, b) => {
      if (!acc[b.category]) acc[b.category] = [];
      acc[b.category]!.push(b);
      return acc;
    }, {}) as Record<BlockCategory, KnowledgeBlock[]>;
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher dans titre + contenu..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
          <Select value={category} onValueChange={(v) => setCategory(v as BlockCategory | 'ALL')}>
            <SelectTrigger className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Toutes catégories</SelectItem>
              {BLOCK_CATEGORIES_ORDER.map((c) => (
                <SelectItem key={c} value={c}>
                  {BLOCK_CATEGORY_LABELS[c]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={(v) => setStatus(v as BlockStatus | 'ALL')}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Tous statuts</SelectItem>
              <SelectItem value="brouillon">Brouillon</SelectItem>
              <SelectItem value="actif">Actif</SelectItem>
              <SelectItem value="archive">Archivé</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setEditorOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> Nouveau bloc
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      )}

      {!isLoading && (!data || data.length === 0) && (
        <Card className="p-12 text-center text-muted-foreground">
          Aucun bloc trouvé. Créez votre premier bloc rédactionnel pour démarrer.
        </Card>
      )}

      {!isLoading && data && data.length > 0 && (
        <div className="space-y-6">
          {BLOCK_CATEGORIES_ORDER.filter((c) => groupedByCategory[c]?.length).map((c) => (
            <div key={c}>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                {BLOCK_CATEGORY_LABELS[c]} ({groupedByCategory[c]!.length})
              </h3>
              <div className="grid gap-3 md:grid-cols-2">
                {groupedByCategory[c]!.map((b) => (
                  <Card key={b.id} className="p-4 flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <h4 className="font-medium">{b.title}</h4>
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                          {b.content}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        <Badge
                          variant={b.status === 'actif' ? 'default' : 'outline'}
                          className="text-xs"
                        >
                          {STATUS_LABELS[b.status]}
                        </Badge>
                      </div>
                    </div>

                    {b.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {b.tags.map((t) => (
                          <Badge key={t} variant="secondary" className="text-xs">
                            {t}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-xs text-muted-foreground">
                        Maj : {new Date(b.updatedAt).toLocaleDateString('fr-FR')}
                        {b.lastUpdatedBy &&
                          ` · ${b.lastUpdatedBy.firstName} ${b.lastUpdatedBy.lastName}`}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            setEditing(b);
                            setEditorOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setConfirmDeleteId(b.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <BlockEditorDialog open={editorOpen} onOpenChange={setEditorOpen} block={editing} />

      <AlertDialog
        open={confirmDeleteId !== null}
        onOpenChange={(o) => !o && setConfirmDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archiver ce bloc ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le bloc ne sera plus disponible pour la rédaction de mémoires.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDeleteId !== null) {
                  deleteBlock.mutate(confirmDeleteId);
                  setConfirmDeleteId(null);
                }
              }}
            >
              Archiver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
